const SUBTAB_BUTTON_CLASS =
    "cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 text-p2 uppercase transition-colors !ease-out hover:text-foreground"
const SUBTAB_TABS_ROW_CLASS = "flex items-center gap-5 py-5 px-5 md:px-0"
const SUBTAB_PANEL_CLASS =
    "size-guide-drawer-content px-5 md:px-0 max-md:pb-5 max-w-[100vw] overflow-x-scroll [&_table]:!mb-0"
const SINGLE_PANEL_CLASS =
    "size-guide-drawer-content px-5 md:px-0 max-md:py-5 max-w-[100vw] overflow-x-scroll [&_table]:!mb-0"

class ProductSizeChartDrawer extends HTMLElement {
    constructor() {
        super()
        this.handleTriggerClick = this.handleTriggerClick.bind(this)
        this.handleKeydown = this.handleKeydown.bind(this)
        this.close = this.close.bind(this)
    }

    connectedCallback() {
        const triggerSelectors = (this.dataset.triggers || "")
            .trim()
            .split(" ")
            .filter(Boolean)

        this.triggers = triggerSelectors
            .map((selector) => Array.from(document.querySelectorAll(selector)))
            .flat()
        this.triggers.forEach((trigger) =>
            trigger.addEventListener("click", this.handleTriggerClick)
        )

        this.querySelectorAll("[data-size-chart-close]").forEach((el) =>
            el.addEventListener("click", this.close)
        )

        this.tabButtons = this.querySelectorAll("[data-size-chart-tab]")
        this.tabButtons.forEach((button) =>
            button.addEventListener("click", () =>
                this.setTab(button.dataset.sizeChartTab)
            )
        )

        this.autoSplitTablePanels()

        this.subtabButtons = this.querySelectorAll("[data-size-chart-subtab]")
        this.subtabButtons.forEach((button) =>
            button.addEventListener("click", () =>
                this.setSubtab(button.dataset.sizeChartSubtab)
            )
        )
    }

    /* Splits a raw rich-text panel (marked with data-size-chart-autosplit)
       into one sub-tab per <table> it contains, matching
       assets/size-guide.js's restructurePanels() approach: walk the
       panel's direct children, track the nearest non-heading text as the
       pending label, and pair it with the <table> that follows it. */
    autoSplitTablePanels() {
        this.querySelectorAll("[data-size-chart-autosplit]").forEach(
            (panel) => {
                const pairs = []
                let pendingLabel = ""

                Array.from(panel.childNodes).forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const tag = node.tagName.toLowerCase()
                        if (tag === "table") {
                            pairs.push({
                                label: pendingLabel.trim(),
                                table: node,
                            })
                            pendingLabel = ""
                            return
                        }
                        if (/^h[1-6]$/.test(tag)) return
                        const text = node.textContent.replace(/ /g, " ").trim()
                        if (text) pendingLabel = text
                        return
                    }
                    if (node.nodeType === Node.TEXT_NODE) {
                        const text = node.textContent.replace(/ /g, " ").trim()
                        if (text) pendingLabel = text
                    }
                })

                if (pairs.length === 0) return
                panel.removeAttribute("data-size-chart-autosplit")

                if (pairs.length === 1) {
                    panel.textContent = ""
                    panel.className = SINGLE_PANEL_CLASS
                    panel.appendChild(pairs[0].table)
                    return
                }

                const tabsRow = document.createElement("div")
                tabsRow.setAttribute("role", "tablist")
                tabsRow.className = SUBTAB_TABS_ROW_CLASS

                const subpanels = pairs.map((pair, index) => {
                    const button = document.createElement("button")
                    button.type = "button"
                    button.setAttribute("role", "tab")
                    button.setAttribute(
                        "aria-selected",
                        index === 0 ? "true" : "false"
                    )
                    button.dataset.sizeChartSubtab = String(index)
                    button.className = `${SUBTAB_BUTTON_CLASS} ${
                        index === 0 ? "text-foreground" : "text-[#14080333]"
                    }`
                    button.textContent = pair.label || String(index + 1)
                    tabsRow.appendChild(button)

                    const subpanel = document.createElement("div")
                    subpanel.setAttribute("role", "tabpanel")
                    subpanel.dataset.sizeChartSubpanel = String(index)
                    subpanel.className = `${SUBTAB_PANEL_CLASS}${
                        index === 0 ? "" : " hidden"
                    }`
                    subpanel.appendChild(pair.table)
                    return subpanel
                })

                panel.textContent = ""
                panel.appendChild(tabsRow)
                subpanels.forEach((subpanel) => panel.appendChild(subpanel))
            }
        )
    }

    handleTriggerClick(event) {
        event.preventDefault()
        event.stopPropagation()

        const trigger = event.currentTarget
        this.open()
        if (trigger.dataset.sizeChartOpenTab) {
            this.setTab(trigger.dataset.sizeChartOpenTab)
        }
        if (trigger.dataset.sizeChartOpenSubtab) {
            this.setSubtab(trigger.dataset.sizeChartOpenSubtab)
        }
    }

    setTab(tab) {
        const body = this.querySelector(".size-chart-drawer-body")
        if (!body) return

        body.dataset.activeTab = tab
        this.tabButtons.forEach((button) => {
            button.setAttribute(
                "aria-selected",
                button.dataset.sizeChartTab === tab ? "true" : "false"
            )
        })
    }

    setSubtab(subtab) {
        this.subtabButtons.forEach((button) => {
            const active = button.dataset.sizeChartSubtab === subtab
            button.classList.toggle("text-foreground", active)
            button.classList.toggle("text-[#14080333]", !active)
            button.setAttribute("aria-selected", active ? "true" : "false")
        })
        this.querySelectorAll("[data-size-chart-subpanel]").forEach((panel) => {
            panel.classList.toggle(
                "hidden",
                panel.dataset.sizeChartSubpanel !== subtab
            )
        })
    }

    open() {
        document
            .querySelectorAll("product-size-chart-drawer.open")
            .forEach((drawer) => {
                if (drawer !== this) drawer.close()
            })

        this.classList.add("open")
        document.addEventListener("keydown", this.handleKeydown)
        document.body.classList.add("!overflow-hidden")
        if (window.lenis) window.lenis.stop()
    }

    close() {
        this.classList.remove("open")
        document.removeEventListener("keydown", this.handleKeydown)
        document.body.classList.remove("!overflow-hidden")
        if (window.lenis) window.lenis.start()
    }

    handleKeydown(event) {
        if (event.key === "Escape") this.close()
    }
}

// Guarded because this file is now loaded from two places — the product page
// and sections/quick-add-popup.liquid, which needs it on every page for the
// size guide inside the popup. A second define() throws and would take the
// rest of that script's execution with it.
if (!customElements.get("product-size-chart-drawer")) {
    customElements.define("product-size-chart-drawer", ProductSizeChartDrawer)
}
